import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "../../../Components/Buttons";
import {
  FormImage,
  FormInput,
  FormSection,
  FormSelect,
  FormSwitch,
  MultiDocumentSelect,
  Toggle,
} from "../../../Components/Form";
import { FormatCommaToArray, formatDateOnly, formatDateTimeLocal, GenerateSystemNumber, ToastMessage, toTagOptions } from "../../../Components/Utils";
import BreadCrumb from "../../../Components/BreadCrumb";
import { ROUTE_PATHS } from "../../../constants/routeConstants";
import { user_profile_image_url } from "../../../Components/ImageUrls";
import { handleError } from "../../../stores/components/handleError";
import { useFetchRolesAllQuery } from "../../../stores/api/roles.api";
import {
  useFetchUnitsAllQuery,
} from "../../../stores/api/units.api";
import unitsHelper from "../../../stores/api/helpers/unit.helper";
import { useFetchAllDistrictsQuery } from "../../../stores/api/district.api";
import { useFetchAllDoctorsQuery } from "../../../stores/api/users.api";
import { BloodGroups, GenderTypes, PatientTypes, WardTypes } from "./Components/PatientSelectsData";
import { editData, initialPatientFormData } from "./Components/FormData";
import { useCreatePatientMutation, useFetchPhysiciansAllQuery, useUpdatePatientMutation } from "../../../stores/api/patient.api";
import patientsHelper from "../../../stores/api/helpers/patients.helper";
import { useFetchAllTalukasQuery, useFetchTalukaByDistrictQuery } from "../../../stores/api/taluka.api";
const formatGender = (gender) => {
  if(gender === "MALE" || gender === "Male" || gender === "male") {
    return "male"
  }
  else if(gender === 'CHILD' || gender === "CHILDE" || gender === "CHILDREN" || gender === "child") {
    return "child";
  }
  else if(gender === "FEMALE" || gender === "Female" || gender === "female") {
    return "female";
  }
  else {
    return "other";
  }
}
const formatAge = (age) => {
  return age.replace(/\s*(Y|YR|YRS|YEAR|YEARS)\s*/gi, '').trim();
};
const toOptions = (list) =>
  (Array.isArray(list) ? list : []).map((item) => ({
    value: String(item.id),
    label: item.name,
  }));
const CreatePatient = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const patientsDetails = location?.state?.patient_details;
  const system_number = location?.state?.system_number || patientsDetails?.system_number || GenerateSystemNumber(0);
  const { data: unitRows = [] } = useFetchUnitsAllQuery();
  const units = useMemo(
    () => (unitRows || []).map((r) => ({ label: r.name, value: r.id, district_id: r.district_id })),
    [unitRows],
  );
 
  const { data: allPhysiciansRow = [] } =
    useFetchAllDoctorsQuery();
  const allPhysicians = useMemo(
    () =>
      (allPhysiciansRow || []).map((r) => ({
        label: r.name,
        value: r.id,
      })),
    [allPhysiciansRow],
  );
  const calculateDob = (age) => {
    const today = new Date();
    return new Date(
        today.getFullYear() - Number(age),
        today.getMonth(),
        today.getDate()
    )
        .toISOString()
        .split("T")[0];
};
  const [createPatient, { isLoading: creating }] = useCreatePatientMutation();
  const [updatePatient, { isLoading: updating }] = useUpdatePatientMutation();
  const loading = creating || updating;
  const [formData, setFormData] = useState(editData(patientsDetails));
  const [checked, setChecked] = useState(formData?.is_referral || patientsDetails?.is_referral || false)
  const [attachments, setAttachments] = useState(patientsDetails?.attachments || []);
  const [existingAttachments, setExistingAttachments] = useState(JSON.stringify(patientsDetails?.attachments) || "");
  const [unitId, setUnitId] = useState(
    formData.unit_id ?? patientsDetails.unit_id
  )
  const [district_id, setSelectedDistrictID] = useState(
    patientsDetails?.unit?.district_id ?? "",
  );
  const { data: districtTalukas } = useFetchTalukaByDistrictQuery(district_id, {
    skip: !district_id,
    refetchOnMountOrArgChange: true,
  })
  const { data: unitPhysicians } = useFetchPhysiciansAllQuery(unitId, {
    skip: !unitId,
    refetchOnMountOrArgChange: true,
  })
  const talukas = useMemo(() => toOptions(districtTalukas), [districtTalukas]);
  const physicians = useMemo(() => toOptions(unitPhysicians), [unitPhysicians]);

  const setField = (name, value) => {
    if (name == "unit_id") {
      const district = units.find((val) => val.value === value);
      if (district?.district_id) {
        setSelectedDistrictID(district?.district_id)
      }
      setUnitId(value)
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  }
  useEffect(() => {
    if (patientsDetails?.attachments) {
      setField("existing_attachments", JSON.stringify(patientsDetails?.attachments))
    }
    if (patientsDetails?.unit?.district?.id) {
      setSelectedDistrictID(patientsDetails?.unit?.district?.id)
    }
    if (system_number) {
      setField("system_number", system_number)
    }
  }, [])
  const [tags, setTags] = useState({
    active_medications: toTagOptions(patientsDetails?.active_medications) || [],
    inactive_medications:toTagOptions(patientsDetails?.inactive_medications) || [],
    significant_medications:toTagOptions(patientsDetails?.significant_medications)|| [],
  });
  const handleTagsInput = (selectedOptions, name) => {
    setTags((prevData) => ({
      ...prevData,
      [name]: selectedOptions || [],
    }));

    const formattedTags = (selectedOptions || [])
      .map((tag) => tag.value || tag.label)
      .join(", ");

    setField(name, formattedTags);
  };
  const handleToggle = (val) => {
    setChecked(val)
    setFormData((prev) => ({ ...prev, is_referral: val }))
  }

  const resetForm = () => {
    setFormData(initialPatientFormData);
    setAttachments([]);
  };
  const handleAttachmentsChange = (
    nextFiles,
    removedFile = null
  ) => {

    if (removedFile?.attachment_file) {
      const updatedExistingAttachments = nextFiles.filter(
        (item) => item?.attachment_file
      );

      setField(
        "existing_attachments",
        JSON.stringify(updatedExistingAttachments)
      );
    }

    const prepared = (nextFiles || []).map((file) => {

      if (file?.attachment_file) {
        return file;
      }

      if (file?.file) {
        return file;
      }

      return {
        name: file.name,
        file,
        preview: file.type?.startsWith("image/")
          ? URL.createObjectURL(file)
          : null,
      };
    });

    setAttachments(prepared);

    setField(
      "attachments",
      prepared
        .filter((item) => item?.file)
        .map((item) => item.file)
    );

    setField(
      "existing_attachments",
      JSON.stringify(
        prepared.filter((item) => item?.attachment_file)
      )
    );
  };
  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        attachments: formData.attachments || attachments.map((item) => item.file),
      };

      if (patientsDetails) {
        await updatePatient(patientsHelper.forUpdate(patientsDetails.id, payload)).unwrap();
      } else {
        await createPatient(patientsHelper.forCreate(payload)).unwrap();
      }
      ToastMessage({
        type: "success",
        title: "Patient Information saved successfully.",
        message: patientsDetails
          ? "Patient updated successfully!"
          : "Patient Registered successfully!",
      });
      if (patientsDetails) {
        navigate(ROUTE_PATHS.PATIENT_LIST)
      }
      if (!patientsDetails) resetForm();
    } catch (err) {
      handleError(err, "Something went wrong");
    }
  };

  return (
    <div className="p-3 sm:p-4">
      <BreadCrumb title={patientsDetails ? "Edit Patient" : "Register Patient"} />

      <form onSubmit={onSubmit} className="mx-auto mt-6 dark:bg-slate-900 rounded-xl p-4 sm:p-6 space-y-5 shadow-sm border border-gray-100 dark:border-slate-800">
        <FormSection title="Patients Details">
          <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
            <FormSelect
              label="Patient Type"
              isRequired

              options={PatientTypes}
              value={formData.patient_type}
              onChange={(option) =>
                setField("patient_type", option || "")
              }
              blurInputOnSelect
            />
          </div>
        </FormSection>

        <FormSection title="Unit & Physician Details" className="pt-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormSelect
              label="Unit"
              isRequired
              options={units}
              value={Number(formData.unit_id)}
              onChange={(val) => setField("unit_id", val ?? "")}
              blurInputOnSelect
            />
            <FormSelect
              label="Physician"
              isRequired
              options={physicians}
              value={formData.physician_id}
              onChange={(val) => setField("physician_id", val ?? "")}
              blurInputOnSelect
            />

          </div>
        </FormSection>

        {formData?.patient_type == "ot" && <><FormSection title="OT Details">
          <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
            <textarea
              name="medical_history"
              rows={4}
              value={formData.medical_history}
              onChange={(e) => setField("medical_history", e.target.value)}
              placeholder="Background Information including relevant medical history"
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            />

            <textarea
              name="referral_to_specialist"
              rows={4}
              value={formData.referral_to_specialist}
              onChange={(e) => setField("referral_to_specialist", e.target.value)}
              placeholder="Reason for referral to specialist OT(must meet criterrial special intervention)"
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* 1st: Active Switch */}
            <div className="flex items-center  gap-3 mt-3">
              <label htmlFor="">Is Referred ?</label>
              <Toggle
                checked={checked}
                onChange={handleToggle}
              />
            </div>

            {/* 2nd: Referred By Button */}
            {checked && <><div className=" cols-span-2 items-center">
              <FormSelect
                label="Referred by"
                isRequired
                options={allPhysicians}
                value={formData.referred_by}
                onChange={(val) => setField("referred_by", val ?? "")}
                blurInputOnSelect
              />
            </div>
            </>}
            {/* 3rd: Operation Date Picker */}
            {checked && <><div className=" gap-3 items-center">
              <FormInput
                label="Operation Date"
                type="date"
                value={formatDateOnly(formData.ot_date)}
                onChange={(val) => setField("ot_date", val)}
              />
            </div></>}
          </div>
        </FormSection></>}

        {formData.patient_type == "icu" || formData.patient_type == "nicu" ? <><FormSection title={formData.patient_type == 'icu' ? "ICU Details" : "NICU Details"}>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {/* Row 1 */}
            <FormInput
              label="Addmission Date Time"
              type="datetime-local"
              value={formatDateTimeLocal(formData.admission_date_time)}
              onChange={(val) => setField("admission_date_time", val)}
            />

            <FormSelect
              label="Ward Type"
              isRequired
              options={WardTypes}
              value={formData.ward_type}
              onChange={(val) => setField("ward_type", val ?? "")}
              blurInputOnSelect
            />

            <FormInput
              label="Bed#"
              type="number"
              value={formData.bed}
              onChange={(val) => setField("bed", val)}
            />

            <div />
          </div>
        </FormSection>

          <FormSection title="">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormInput
                label="Date of Previous Admission"
                type="datetime-local"
                value={formatDateTimeLocal(formData.previous_admission_date)}
                onChange={(val) => setField("previous_admission_date", val)}
              />

              <FormInput
                label="Date of Previous Discharge"
                type="datetime-local"
                value={formatDateTimeLocal(formData.previous_discharge_date)}
                onChange={(val) => setField("previous_discharge_date", val)}
              />
            </div>
          </FormSection>

          <FormSection title="">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pt-6">
              {/* Row 3 - Full width textarea */}
              <div className="col-span-3">
                <textarea
                  name="note_special_circumstances"
                  rows={4}
                  value={formData.note_special_circumstances}
                  onChange={(e) => setField("note_special_circumstances", e.target.value)}
                  placeholder="Care Note/Special Circumstances"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

            </div>
          </FormSection></> : <></>}


        <FormSection title="Patient Details">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            <FormInput
              label="Patient name"
              placeholder="Enter patient name"
              value={formData.patient_name}
              onChange={(val) => setField("patient_name", val)}
            />
            <FormInput
              label="Father/Husband name"
              placeholder="Enter father or husband name"
              value={formData.father_husband_name}
              onChange={(val) => setField("father_husband_name", val)}
            />

            <FormInput
              label="Registration Date"
              type="datetime-local"
              placeholder="Select registration date"
              value={formatDateTimeLocal(formData.reg_date)}
              onChange={(val) => setField("reg_date", val)}
            />
          </div>
        </FormSection>

        <FormSection>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {/* Row 1 - 3 fields */}
            <FormSelect
              label=" Taluka"
              isRequired
              options={talukas}
              value={formData.taluka_id}
              onChange={(val) => setField("taluka_id", val ?? "")}
              blurInputOnSelect
            />
            <FormInput
              label="Location"
              placeholder="Enter location"
              value={formData.location}
              onChange={(v) => setField("location", v)}
            />
            <FormInput
              label="Distance"
              placeholder="Enter distance"
              value={formData.distance}
              onChange={(v) => setField("distance", v)}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {/* Row 2 - 3 fields */}
            <FormInput
              label="Age"
              placeholder="Enter age"
              value={formatAge(formData.age)}
              type="number"
              onChange={(v) => setField("age", v)}
            />
            <FormInput
              label="Date of birth"
              type="date"
              value={formatDateOnly(formData.dob)}
              onChange={(v) => setField("dob", v)}
            />
            <FormSelect
              label="Gender"
              isRequired
              options={GenderTypes}
              value={formatGender(formData.gender)}
              onChange={(val) => setField("gender", val ?? "")}
              blurInputOnSelect
            />

            <FormInput
              label="Patient CNIC"
              placeholder="e.g. 35202-1234567-1"
              value={formData.patient_cnic}
              addMasking
              maskFor="cnic"
              onChange={(v) => setField("patient_cnic", v)}
            />
          </div>
        </FormSection>

        <FormSection>
          <div>
            <textarea
              rows={4}
              value={formData.address}
              onChange={(e) => setField("address", e.target.value)}
              className="w-full border rounded px-3 py-2"
              placeholder="Address Details"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <FormInput
              label="Mobile/Phone Number"
              placeholder="e.g. 0300-1234567"
              value={formData.mobile}
              addMasking
              maskFor="mobile"
              onChange={(v) => setField("mobile", v)}
            />
            <FormInput
              label="Any GOVT.ID"
              placeholder="Enter government ID"
              value={formData.any_govt_id}
              onChange={(v) => setField("any_govt_id", v)}
            />
            <FormInput
              label="Prescription Pad No"
              placeholder="Enter prescription pad number"
              value={formData.prescription_pad_no}
              onChange={(v) => setField("prescription_pad_no", v)}
            />
            <FormInput
              label="System Number"
              readonly={true}
              value={formData.system_number}

            />
            <div className="sm:col-span-2 xl:col-span-4">
              <textarea
                rows={4}
                value={formData.diagnosis}
                onChange={(e) => setField("diagnosis", e.target.value)}
                className="w-full border rounded px-3 py-2"
                placeholder="Diagnosis"
              />
              <textarea
                rows={4}
                value={formData.other_identifiers}
                onChange={(e) => setField("other_identifiers", e.target.value)}
                className="w-full border rounded px-3 py-2"
                placeholder="Other Identifiers"
              />
            </div>
          </div>
        </FormSection>



        <FormSection title="Vital Signs">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">

            {/* Row 1 - 4 columns */}
            <FormInput
              label="Temperature (°F)"
              type="number"
              value={formData.temperature}
              onChange={(val) => setField("temperature", val)}
            />

            <FormInput
              label="Height (Feet/Inches)"
              type="number"
              value={formData.height}
              onChange={(val) => setField("height", val)}
            />

            <FormInput
              label="Weight (kg)"
              type="number"
              value={formData.weight}
              onChange={(val) => setField("weight", val)}
            />
            <FormInput
              label="Pulse (/min)"
              type="number"
              value={formData.pulse}
              onChange={(val) => setField("pulse", val)}
            />

          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
            <FormInput
              label="BP (mmHg)"
              value={formData.bp}
              onChange={(val) => setField("bp", val)}
            />
            <FormSelect
              label="Blood Group"
              isRequired
              options={BloodGroups}
              value={formData.blood_group}
              onChange={(val) => setField("blood_group", val ?? "")}
              blurInputOnSelect
            />
            <FormInput
              label="RR (/min)"
              type="number"
              value={formData.rr}
              onChange={(val) => setField("rr", val)}
            />
          </div>
        </FormSection>


        <FormSection title="Medications">
          <div className="grid grid-cols-1 gap-4">

            <FormInput
              type="tags"
              isMulti={true}
              label={"Active Medications"}
              isRequired
              value={tags?.active_medications}
              onChange={(val) => {
                handleTagsInput(val, "active_medications")
              }}
              placeholder="Type and press ENTER key..."
            />
            <FormInput
              label="In-Active Medications"
              type="tags"
              isMulti={true}
              isRequired
              value={tags.inactive_medications}
              onChange={(val) => {
                handleTagsInput(val, "inactive_medications")
              }}
            />
            <FormInput
              label="Significant Medications"
              type="tags"
              isMulti={true}
              isRequired
              value={tags.significant_medications}
              onChange={(val) => {
                handleTagsInput(val, "significant_medications")
              }}
            />
            <textarea
              rows={3}
              value={formData.alert_intolerance}
              onChange={(e) => setField("alert_intolerance", e.target.value)}
              placeholder="Intolerance/ Allergies"
              className="w-full border rounded px-3 py-2"
            />

            <textarea
              rows={3}
              value={formData.visit_history}
              onChange={(e) => setField("visit_history", e.target.value)}
              placeholder="Visit History"
              className="w-full border rounded px-3 py-2"
            />

            <textarea
              rows={3}
              value={formData.chief_complaints}
              onChange={(e) => setField("chief_complaints", e.target.value)}
              placeholder="Chief Complaints"
              className="w-full border rounded px-3 py-2"
            />

            <textarea
              rows={3}
              value={formData.care_plan_notes}
              onChange={(e) => setField("care_plan_notes", e.target.value)}
              placeholder="Care Plan / Notes"
              className="w-full border rounded px-3 py-2"
            />



          </div>
        </FormSection>
        <FormSection title="Attachments">
          <MultiDocumentSelect
            label="Investigations / Lab Results"
            files={attachments}
            onChange={handleAttachmentsChange}
          />
        </FormSection>
        <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-3 pt-2">
          <Button
            type="button"
            onClick={() => {
              resetForm();
              navigate(ROUTE_PATHS.PATIENT_LIST);
            }}
            className="!bg-gray-100 !text-gray-700 hover:!bg-gray-200 dark:!bg-slate-800 dark:!text-gray-200"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="!bg-green-500 !text-white hover:!bg-green-600"
            loading={loading}
            disabled={loading}
            loadingText={patientsDetails ? "Updating..." : "Registering..."}
          >
            {patientsDetails ? "Update Patient" : "Register Patient"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CreatePatient;
